import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { tr } from "@/lib/i18n";
import { mpTrack } from "@/lib/metaPixel";
import { trackEvent } from "@/lib/analytics";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("pending");
  const navigate = useNavigate();
  const { refreshAccess, lang } = useAuth();
  const t = (k) => tr(k, lang);
  const purchaseTracked = useRef(false);

  useEffect(() => {
    if (!sessionId) { navigate("/pricing"); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          await refreshAccess();
          if (!purchaseTracked.current && r.data.amount != null && r.data.currency) {
            purchaseTracked.current = true;
            trackEvent("checkout_completed", {
              plan: r.data.lookup_key,
              price: r.data.amount,
              currency: (r.data.currency || "").toUpperCase(),
              session_id: sessionId,
            });
            mpTrack("Purchase", {
              value: r.data.amount,
              currency: r.data.currency,
              content_ids: r.data.lookup_key ? [r.data.lookup_key] : undefined,
              content_type: "product",
            });
          }
          return;
        }
        if (["failed","expired","refunded"].includes(r.data.payment_status)) {
          setStatus(r.data.payment_status); return;
        }
      } catch { /* ignore */ }
      if (attempts >= 20) { setStatus("timeout"); return; }
      setTimeout(poll, 1500);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, navigate, refreshAccess]);

  return (
    <div className="max-w-lg mx-auto text-center pt-10">
      {status === "pending" && (
        <>
          <Loader2 className="mx-auto animate-spin text-amber-500" size={40}/>
          <div className="serif text-3xl text-stone-100 mt-4">{t("pay.confirming")}</div>
          <div className="mono text-xs text-stone-500 mt-2 tracking-widest">{t("pay.wait")}</div>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="mx-auto text-emerald-400" size={48}/>
          <div className="serif text-4xl text-stone-100 mt-4">{t("pay.done")}</div>
          <div className="text-stone-400 mt-2">{t("pay.done_body")}</div>
          <div className="mt-8 flex gap-3 justify-center">
            <button data-testid="success-to-dashboard" onClick={()=>navigate("/dashboard")} className="pill-btn">{t("pay.to_dash")}</button>
            <button onClick={()=>navigate("/scenes/new")} className="ghost-btn">{t("pay.new_scene")}</button>
          </div>
        </>
      )}
      {(status === "failed" || status === "expired" || status === "timeout") && (
        <>
          <XCircle className="mx-auto text-rose-400" size={48}/>
          <div className="serif text-3xl text-stone-100 mt-4">{t("pay.fail")}</div>
          <div className="text-stone-400 mt-2">{t("pay.retry")}</div>
          <button onClick={()=>navigate("/pricing")} className="pill-btn mt-6">{t("pay.choose")}</button>
        </>
      )}
    </div>
  );
}
